/**
 * @import { MapProps } from '@vis.gl/react-google-maps'
 */

import { config } from '@/config/config';
import { Splitter } from '@/ui/splitter/splitter';
import { createPeakPanelState } from '@/utils/panel';
import { Map } from '@vis.gl/react-google-maps';
import { useRef, useState } from 'react';
import { useMapViewportFilterContext } from '../../context/map-viewport-filter';
import { useBounds } from '../../hooks/use-bounds';
import { Cruises, CruisesHeader } from '../cruises';
import { FiltersModal } from '../filters-modal/filters-modal';
import { Legend } from '../legend/legend';
import { ModalProvider } from '../modal-provider';
import { PlacesLayer } from '../places-layer';
import { MapTypeControl } from '@/ui/map-type-control/map-type-control';
import { BackToPreviousControl } from '../back-to-previous-control/back-to-previous-control';
import classes from './mobile-map.module.css';

const estimatedCardHeight = 215;
const panelStates = [['100%', '0%'], createPeakPanelState(estimatedCardHeight), ['10%', '90%']];

/**
 * @param {object} props
 * @param {MapProps} props.mapProps
 */
export function MobileMap({ mapProps }) {
  const { setMapViewportFilter } = useMapViewportFilterContext();
  const [panelIndex, setPanelIndex] = useState(1);
  useBounds();

  /** @type {React.Ref<HTMLDivElement | null>} */
  const modalContainerRef = useRef(null);

  const snapDrawer = (/** @type {React.UIEvent} */ event) => {
    event.preventDefault();
    const nextIndex = Math.max(1, (panelIndex + 1) % panelStates.length);
    setPanelIndex(nextIndex);
  };

  const handleResizeStart = () => {
    setMapViewportFilter({ disabled: true });
  };

  const handleResizeEnd = async (/** @type {{ snapIndex: number }} */ event) => {
    if (event.snapIndex >= 0) {
      setPanelIndex(event.snapIndex);
    }

    // Avoid re-enabling the map viewport filter too quickly because the map viewport changes as a result
    // of the resize and we don't want that to trigger a cruise search as a result of that change.  Doing so
    // would disturb (i.e. constrain) the cruises for the intended area the user was looking at.
    await new Promise((resolve) => setTimeout(resolve, 100));

    setMapViewportFilter({ disabled: false });
  };

  return (
    <ModalProvider>
      <div ref={modalContainerRef} className={classes.mobileMap}>
        <Splitter
          panelSizes={panelStates[panelIndex]}
          snapPanelSizes={panelStates}
          onResizeStart={handleResizeStart}
          onResizeEnd={handleResizeEnd}
          onHandleClick={snapDrawer}
          gutterSlot={<CruisesHeader />}
          disableDrag={config.disableDrawerDrag}
        >
          <Map style={{ height: '100%' }} {...mapProps}>
            <PlacesLayer />
            <BackToPreviousControl />
            <Legend />
            <MapTypeControl />
          </Map>
          <Cruises />
        </Splitter>
        <FiltersModal container={modalContainerRef} />
      </div>
    </ModalProvider>
  );
}
